import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import type { PopperProps, SxProps, Theme } from '@mui/material';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Popper,
    TextField,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortAlphabetically } from 'ui/DropdownItems/helpers';
import Flex from 'ui/Flex/Flex';

import type { AutocompletAble } from './types';

type Props<T> = {
    selectedValues: AutocompletAble<T>[];
    availableValues: AutocompletAble<T>[];
    setSelectedValues: DispatchState<AutocompletAble<T>[]>;
    label?: string;
    sort?: boolean;
    onClose?: () => void;
    onConfirm?: () => void;
    sx?: SxProps<Theme>;
    confirmCondition?: boolean;
    sortOptions?: (options: AutocompletAble<T>[]) => AutocompletAble<T>[];
};

const CustomPopper = (props: PopperProps) => (
    <Popper {...props} open={props.open} sx={{ position: 'relative' }} />
);

const OrderedAutocompleteWithConfirm = <T,>({
    availableValues,
    selectedValues,
    setSelectedValues,
    label = '',
    onClose = () => {},
    sort = true,
    onConfirm = () => {},
    sx = { m: '2rem 0' },
    confirmCondition = true,
    sortOptions = sortAlphabetically,
}: Props<T>) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);

    const getOptions = (options: AutocompletAble<T>[]) =>
        sort ? sortOptions(options) : options;

    // without this useMemo, there is a re-render on first check of an option
    const options = useMemo(
        () => getOptions(availableValues),
        [JSON.stringify(availableValues)],
    );

    return (
        <Box sx={sx}>
            <Autocomplete
                disableClearable
                disableCloseOnSelect
                multiple
                open={open}
                openText={t('common.open')}
                closeText={t('common.close')}
                options={options}
                getOptionLabel={(option) => option.label ?? ''}
                getOptionDisabled={(option) => !!option.disabled}
                value={selectedValues}
                limitTags={3}
                onOpen={() => setOpen(true)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        color="primary"
                        label={label}
                    />
                )}
                isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                }
                renderOption={(props, id, { selected }) => (
                    <Fragment key={id.label}>
                        <li
                            {...props}
                            style={{
                                paddingBottom: id.isAll ? '10px' : '0px',
                            }}
                        >
                            <Checkbox
                                icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                disabled={id.disabled}
                            />
                            {id.label}
                        </li>
                        {options.indexOf(id) === options.length - 1 && (
                            <li>
                                <Flex justifyContent="space-between">
                                    <Button
                                        onClick={() => {
                                            onClose();
                                            setOpen(false);
                                        }}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                    <Button
                                        disabled={!confirmCondition}
                                        variant="contained"
                                        onClick={() => {
                                            onConfirm();
                                            setOpen(false);
                                        }}
                                    >
                                        {t('common.confirm')}
                                    </Button>
                                </Flex>
                            </li>
                        )}
                    </Fragment>
                )}
                renderTags={(ids) =>
                    ids.map((id) => (
                        <Chip
                            key={id.label}
                            color="primary"
                            label={`${selectedValues.indexOf(id) + 1} - ${
                                id.label
                            }`}
                        />
                    ))
                }
                onChange={(_, ids) => {
                    setSelectedValues(ids);
                }}
                onClose={() => {
                    onClose();
                    setOpen(false);
                }}
                slots={{ popper: CustomPopper }}
            />
        </Box>
    );
};

export default OrderedAutocompleteWithConfirm;
